import React from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  useTheme,
} from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import DatePicker2 from "../../../../../../views/components/common/textFields/DatePicker2";
import dayjs, { Dayjs } from "dayjs";

interface CommonExpectedDatesDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  data: Array<{ [key: string]: any }>;
  image?: string;
  isLoading: boolean;
  onUpdate: () => void;
  onDateChange: (newValue: Dayjs | null, index: number) => void;
  type: "rawInward" | "printInward";
}

const CommonExpectedDatesDialog: React.FC<CommonExpectedDatesDialogProps> = ({
  open,
  onClose,
  title,
  data,
  image,
  isLoading,
  onUpdate,
  onDateChange,
  type,
}) => {
  const theme = useTheme();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ padding: 2 }}>
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: type === "rawInward" ? "1.2rem" : "1rem",
          }}
        >
          {title}
        </DialogTitle>
        <DialogContent
          sx={{
            padding: "10px 24px",
            height: type === "rawInward" ? "300px" : "280px",
          }}
        >
          {data?.length ? (
            data.map((item, index) => (
              <Card key={index} sx={{ mb: 3, p: 1, borderRadius: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    m: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 45,
                        height: 45,
                        backgroundColor:
                          theme.palette.customColors.grayBackGroundColor,
                        borderRadius: "30%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={image}
                        alt="Item Icon"
                        style={{
                          width: 34,
                          height: 34,
                          objectFit: "cover",
                          borderRadius: "50%",
                        }}
                      />
                    </Box>
                    <Box>
                      {type === "rawInward" ? (
                        <>
                          <CustomTypography
                            variant="heading7"
                            fontWeight={600}
                            style={{ paddingBottom: 4 }}
                          >
                            PO ID: {item.id}
                          </CustomTypography>
                          <CustomTypography variant="heading8" fontWeight={400}>
                            PR ID: {item.pr_id}
                          </CustomTypography>
                        </>
                      ) : (
                        <>
                          <CustomTypography
                            variant="heading7"
                            fontWeight={600}
                            style={{ paddingBottom: 4 }}
                          >
                            PO ID: {item.po_id}
                          </CustomTypography>
                          <CustomTypography variant="heading8" fontWeight={400}>
                            Raw Inward ID: {item.raw_inward_id}
                          </CustomTypography>
                        </>
                      )}
                    </Box>
                  </Box>
                  {type === "rawInward" && (
                    <CustomTypography variant="heading7" fontWeight={600}>
                      <span
                        style={{
                          color: theme.palette.customColors.greyDarkborder,
                        }}
                      >
                        Qty:
                      </span>{" "}
                      {item.quantity || 0}
                    </CustomTypography>
                  )}
                  <Box sx={{ width: "28%" }}>
                    <DatePicker2
                      label="Expected Date"
                      value={
                        type === "rawInward"
                          ? item.expected_delivery_date
                            ? dayjs(item.expected_delivery_date)
                            : null
                          : item.timeline
                          ? dayjs(item.timeline)
                          : null
                      }
                      onChange={(newValue) => onDateChange(newValue, index)}
                    />
                  </Box>
                </Box>
              </Card>
            ))
          ) : (
            <CustomTypography variant="body2" color="text.secondary">
              {type === "rawInward"
                ? "No purchase orders available."
                : "No print inward timelines available."}
            </CustomTypography>
          )}
        </DialogContent>
        <DialogActions
          sx={{ alignItems: "center", justifyContent: "center", pt: 2 }}
        >
          <AppButton
            label="Cancel"
            variant="grayscale"
            onClick={onClose}
            customStyles={{
              fontWeight: 700,
              width: "30%",
              borderRadius: "20px",
            }}
          />
          <AppButton
            label={isLoading ? "Updating..." : "Update"}
            onClick={onUpdate}
            variant="primary"
            disabled={isLoading}
            customStyles={{ width: "30%", borderRadius: "20px" }}
          />
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CommonExpectedDatesDialog;
